import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Tooltip, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { getFileSize } from '../../../../utils';
import { UPLOAD_TYPE, normalizeUploadPath, toNFC } from '../apiServices';
import { isRecentActivity } from '../liveUploads';

// Files shown per expanded folder before "Show more"
const FOLDER_PAGE_SIZE = 100;

const IGNORED_FILES = ['.DS_Store', 'Thumbs.db', 'desktop.ini'];
// macOS resource forks ("._x") and Office lock files ("~$x")
const IGNORED_PREFIXES = ['._', '~$'];

const isIgnored = (name) => IGNORED_FILES.includes(name) || IGNORED_PREFIXES.some(prefix => name.startsWith(prefix));

// readEntries returns ~100 entries per call, so read until empty
const readAllEntries = (reader) => new Promise((resolve, reject) => {
  const entries = [];
  const readBatch = () => reader.readEntries((batch) => {
    if (!batch.length) {
      resolve(entries);
    } else {
      entries.push(...batch);
      readBatch();
    }
  }, reject);
  readBatch();
});

const collectEntryFiles = async (entry, out) => {
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    if (!isIgnored(file.name)) {
      out.push({ file, relativePath: toNFC(entry.fullPath.replace(/^\//, '')) });
    }
  } else if (entry.isDirectory) {
    const children = await readAllEntries(entry.createReader());
    for (const child of children) {
      await collectEntryFiles(child, out);
    }
  }
};

const fromFileList = (fileList) => Array.from(fileList)
  .filter(file => !isIgnored(file.name))
  .map(file => ({ file, relativePath: toNFC(file.webkitRelativePath || file.name) }));

const RESUME_PROBLEM_PREVIEW = 5;
const DONE_FILE_STATUSES = ['COMPLETED', 'UPLOADED'];
// Most files a resume may be short before it's treated as a different selection
const MAX_RESUME_SHORTFALL = 10;

// Checks a resume selection against the session's files. Unknown or missing files block it.
// Partly uploaded large files are restarted, since a different file of the same size can't be told apart.
const getResumeCheck = (resumeSession, sessionFiles, items) => {
  if (!resumeSession || !items.length || !sessionFiles) return null;
  const expectedFiles = Number(resumeSession.total_files) || 0;
  const byPath = new Map(sessionFiles.map(file => [normalizeUploadPath(file.path), file]));
  const selectedKeys = new Set();
  const changed = [];
  const partial = [];
  const unregistered = [];
  let unregisteredBytes = 0;
  let done = 0;
  items.forEach(({ file, relativePath }) => {
    const key = normalizeUploadPath(relativePath);
    selectedKeys.add(key);
    const remote = byPath.get(key);
    if (!remote) {
      unregistered.push(relativePath);
      unregisteredBytes += file.size;
      return;
    }
    if (DONE_FILE_STATUSES.includes(remote.status)) {
      done += 1;
    } else if (file.size !== remote.size) {
      changed.push(relativePath);
    } else if (remote.uploadType === UPLOAD_TYPE.MULTIPART && (remote.uploadedBytes > 0 || remote.status !== 'PENDING')) {
      partial.push(relativePath);
    }
  });
  // Finished files don't need to be reselected
  const missing = sessionFiles.filter(file => !DONE_FILE_STATUSES.includes(file.status) && !selectedKeys.has(normalizeUploadPath(file.path))).map(file => file.path);
  // Fully registered: unknown selected files (e.g. skipped duplicates) are left out, not blocking
  const fullyRegistered = sessionFiles.length >= expectedFiles;
  const leftOut = fullyRegistered ? unregistered : [];
  // Partly registered: unknown files may be the unregistered ones, so up to that many are allowed.
  // A few fewer lowers the session's totals; many fewer means a different selection.
  const unregisteredAllowed = Math.max(0, expectedFiles - sessionFiles.length);
  const extra = !fullyRegistered && unregistered.length > unregisteredAllowed ? unregistered : [];
  const shortfall = !fullyRegistered && !extra.length ? unregisteredAllowed - unregistered.length : 0;
  const tooFew = shortfall > MAX_RESUME_SHORTFALL;
  const adjustTotals = shortfall > 0 && !tooFew && !missing.length ? {
    totalFiles: sessionFiles.length + unregistered.length,
    totalBytes: sessionFiles.reduce((sum, file) => sum + (file.size || 0), 0) + unregisteredBytes,
  } : null;
  const countMatches = fullyRegistered || (!extra.length && !tooFew);
  // Recently active UPLOADING files mean someone else is still uploading this session
  const activeElsewhere = sessionFiles.filter(file => file.status === 'UPLOADING' && isRecentActivity(file.updatedAt)).map(file => file.path);
  return {
    blocked: !countMatches || !!extra.length || !!missing.length || !!activeElsewhere.length,
    countMatches, expectedFiles, extra, missing, changed, partial, activeElsewhere, leftOut,
    shortfall: adjustTotals ? shortfall : 0, adjustTotals,
    restart: [...changed, ...partial],
    done: sessionFiles.filter(file => DONE_FILE_STATUSES.includes(file.status)).length,
    toUpload: items.length - done - leftOut.length,
  };
};

const ProblemList = ({ title, paths }) => paths && paths.length ? (
  <div className="stream-upload-modal__problem-list">
    <b>{title} ({paths.length.toLocaleString()})</b>
    {paths.slice(0, RESUME_PROBLEM_PREVIEW).map(path => <div key={path} className="stream-upload-modal__problem-path">{path}</div>)}
    {paths.length > RESUME_PROBLEM_PREVIEW ? <div>and {(paths.length - RESUME_PROBLEM_PREVIEW).toLocaleString()} more</div> : null}
  </div>
) : null;

const StreamUploadModal = ({ dataRoom, open, onClose, onStartUpload, starting = false, resumeSession = null, resumeFiles = null, onRetryResumeFiles }) => {
  const [items, setItems] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [visibleCounts, setVisibleCounts] = useState({});
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // React 17 doesn't forward webkitdirectory; a callback ref because the Dialog renders in a portal
  const setFolderInputRef = useCallback((node) => {
    folderInputRef.current = node;
    if (node) {
      node.setAttribute('webkitdirectory', '');
      node.setAttribute('directory', '');
    }
  }, []);

  const addItems = (newItems) => {
    setItems(prevItems => {
      const byPath = new Map(prevItems.map(item => [item.relativePath, item]));
      newItems.forEach(item => byPath.set(item.relativePath, item));
      return Array.from(byPath.values());
    });
  };

  const onInputChange = (evt) => {
    addItems(fromFileList(evt.target.files));
    evt.target.value = ''; // allow re-selecting the same folder
  };

  const onDrop = async (evt) => {
    evt.preventDefault();
    setDragActive(false);
    // Read entries before the first await; the DataTransfer is cleared afterwards
    const entries = Array.from(evt.dataTransfer.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.webkitGetAsEntry ? item.webkitGetAsEntry() : null);
    if (!entries.length || entries.some(entry => !entry)) {
      addItems(fromFileList(evt.dataTransfer.files));
      return;
    }
    setScanning(true);
    try {
      const collected = [];
      for (const entry of entries) {
        await collectEntryFiles(entry, collected);
      }
      addItems(collected);
    } finally {
      setScanning(false);
    }
  };

  // Group by top-level folder so 10k files render as a handful of rows
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach(({ file, relativePath }) => {
      const parts = relativePath.split('/');
      const isFolder = parts.length > 1;
      const name = parts[0];
      const key = `${isFolder ? 'd' : 'f'}:${name}`;
      const group = map.get(key) || { key, name, isFolder, count: 0, size: 0, files: [] };
      group.count += 1;
      group.size += file.size;
      group.files.push({ file, relativePath, subPath: parts.slice(1).join('/') });
      map.set(key, group);
    });
    const sorted = Array.from(map.values()).sort((a, b) => (b.isFolder - a.isFolder) || a.name.localeCompare(b.name));
    sorted.forEach(group => group.files.sort((a, b) => a.relativePath.localeCompare(b.relativePath)));
    return sorted;
  }, [items]);

  const folderCount = groups.filter(group => group.isFolder).length;

  const totalSize = useMemo(() => items.reduce((sum, item) => sum + item.file.size, 0), [items]);

  const removeGroup = (group) => {
    setItems(prevItems => prevItems.filter(({ relativePath }) => {
      const parts = relativePath.split('/');
      return !(parts[0] === group.name && (parts.length > 1) === group.isFolder);
    }));
  };

  const removeFile = (relativePath) => {
    setItems(prevItems => prevItems.filter(item => item.relativePath !== relativePath));
  };

  const clearAll = () => {
    setItems([]);
    setExpanded({});
    setVisibleCounts({});
  };

  const handleClose = () => {
    clearAll();
    onClose();
  };

  const folderName = dataRoom && dataRoom.navigation && dataRoom.navigation.length ? dataRoom.navigation[dataRoom.navigation.length - 1].name : 'loading...';
  const checkingResume = !!(resumeSession && (!resumeFiles || resumeFiles.loading));
  const resumeFilesFailed = !!(resumeSession && resumeFiles && !resumeFiles.loading && !resumeFiles.files);
  const resumeCheck = resumeSession && !checkingResume && !resumeFilesFailed ? getResumeCheck(resumeSession, resumeFiles.files, items) : null;
  const restartPaths = new Set(resumeCheck ? resumeCheck.restart : []);
  const leftOutPaths = new Set(resumeCheck ? resumeCheck.leftOut : []);
  // Resume registers only the session's own files
  const startUpload = () => onStartUpload(items
    .filter(item => !leftOutPaths.has(item.relativePath))
    .map(item => (restartPaths.has(item.relativePath) ? { ...item, restart: true } : item)),
    { adjustTotals: resumeCheck ? resumeCheck.adjustTotals : null });

  return (
    <Dialog className="stream-upload-modal" open={open} onClose={scanning || starting ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {resumeSession ?
          <>Resume Upload{resumeSession.asset_name ? <> To "<b>{resumeSession.asset_name}</b>"</> : null}</> :
          <>Stream Upload To "<b>{folderName}</b>"</>
        }
      </DialogTitle>
      <DialogContent dividers>
        {resumeSession ?
          <Alert severity="info" className="stream-upload-modal__notice">
            Select the same files or folder you originally uploaded: <b>{(Number(resumeSession.total_files) || 0).toLocaleString()} files ({getFileSize(Number(resumeSession.total_bytes) || 0)})</b>. Files that already finished will be skipped and don't need to be selected again.
          </Alert> : null
        }
        {resumeFilesFailed ?
          <Alert
            severity="error"
            className="stream-upload-modal__notice"
            action={onRetryResumeFiles ? <Button color="inherit" size="small" onClick={onRetryResumeFiles}>Try Again</Button> : null}
          >
            Couldn't load this upload's file list, so the selection can't be checked yet.
          </Alert> : null
        }
        <div
          className={`stream-upload-modal__drop-area${items.length ? ' stream-upload-modal__drop-area--compact' : ''}${dragActive ? ' stream-upload-modal__drop-area--active' : ''}`}
          onDragOver={(evt) => { evt.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
        >
          {items.length ? (
            <Typography variant="body2" color="textSecondary">
              <CloudUploadIcon className="stream-upload-modal__upload-icon-compact" />Drop more files or folders here
            </Typography>
          ) : (
            <>
              <CloudUploadIcon className="stream-upload-modal__upload-icon" />
              <Typography variant="body1">Drag & drop files or folders here</Typography>
              <Typography variant="caption" color="textSecondary">or</Typography>
            </>
          )}
          <div className={items.length ? 'stream-upload-modal__pick-buttons stream-upload-modal__pick-buttons--compact' : 'stream-upload-modal__pick-buttons'}>
            <Button disabled={scanning} onClick={() => fileInputRef.current.click()} variant="outlined" color="primary" size="small" startIcon={<NoteAddIcon />}>Select Files</Button>
            <Button disabled={scanning} onClick={() => folderInputRef.current.click()} variant="outlined" color="primary" size="small" startIcon={<CreateNewFolderIcon />}>Select Folder</Button>
          </div>
          <input ref={fileInputRef} type="file" multiple hidden onChange={onInputChange} />
          <input ref={setFolderInputRef} type="file" multiple hidden onChange={onInputChange} />
        </div>
        {scanning ? (
          <div className="stream-upload-modal__scanning">
            <Typography variant="caption" color="textSecondary">Reading folder contents...</Typography>
            <LinearProgress />
          </div>
        ) : null}
        {items.length ? (
          <>
            <div className="stream-upload-modal__summary">
              <Typography variant="subtitle2">
                {folderCount ? `${folderCount.toLocaleString()} folder${folderCount > 1 ? 's' : ''} · ` : ''}
                {items.length.toLocaleString()} file{items.length > 1 ? 's' : ''} selected ({getFileSize(totalSize)})
              </Typography>
              <Button size="small" onClick={clearAll}>Clear All</Button>
            </div>
            <div className="stream-upload-modal__list">
              {groups.map(group => {
                if (!group.isFolder) {
                  const { relativePath, file } = group.files[0];
                  return (
                    <div key={group.key} className="stream-upload-modal__group-row stream-upload-modal__group-row--file">
                      <InsertDriveFileIcon color="primary" fontSize="small" className="stream-upload-modal__group-icon" />
                      <Typography variant="body2" className="stream-upload-modal__group-name" title={relativePath}>{group.name}</Typography>
                      <span className="stream-upload-modal__meta">{getFileSize(file.size)}</span>
                      <Tooltip title="Remove" arrow>
                        <IconButton size="small" onClick={() => removeFile(relativePath)}><CloseIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </div>
                  );
                }
                // A lone folder opens by default, there is nothing else to scan past
                const isOpen = expanded[group.key] !== undefined ? expanded[group.key] : groups.length === 1;
                const visible = visibleCounts[group.key] || FOLDER_PAGE_SIZE;
                const remaining = group.files.length - visible;
                return (
                  <div key={group.key}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      className={`stream-upload-modal__group-row${isOpen ? ' stream-upload-modal__group-row--open' : ''}`}
                      onClick={() => setExpanded(prev => ({ ...prev, [group.key]: !isOpen }))}
                      onKeyDown={(evt) => { if (evt.key === 'Enter' || evt.key === ' ') { evt.preventDefault(); setExpanded(prev => ({ ...prev, [group.key]: !isOpen })); } }}
                    >
                      <ChevronRightIcon className={`stream-upload-modal__chevron${isOpen ? ' stream-upload-modal__chevron--open' : ''}`} />
                      {isOpen ? <FolderOpenIcon color="primary" fontSize="small" className="stream-upload-modal__group-icon" /> : <FolderIcon color="primary" fontSize="small" className="stream-upload-modal__group-icon" />}
                      <Typography variant="body2" className="stream-upload-modal__group-name" title={group.name}>{group.name}</Typography>
                      <span className="stream-upload-modal__meta">{group.count.toLocaleString()} file{group.count > 1 ? 's' : ''} · {getFileSize(group.size)}</span>
                      <Tooltip title="Remove folder" arrow>
                        <IconButton size="small" onClick={(evt) => { evt.stopPropagation(); removeGroup(group); }}><DeleteOutlineIcon fontSize="small" color="error" /></IconButton>
                      </Tooltip>
                    </div>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      {group.files.slice(0, visible).map(({ file, relativePath, subPath }) => {
                        const slash = subPath.lastIndexOf('/');
                        return (
                          <div key={relativePath} className="stream-upload-modal__file-row">
                            <InsertDriveFileIcon className="stream-upload-modal__file-icon" />
                            <span className="stream-upload-modal__file-name" title={relativePath}>
                              {slash > -1 ? <span className="stream-upload-modal__file-dir">{subPath.slice(0, slash + 1)}</span> : null}
                              {file.name}
                            </span>
                            <span className="stream-upload-modal__meta">{getFileSize(file.size)}</span>
                            <Tooltip title="Remove" arrow>
                              <IconButton size="small" onClick={() => removeFile(relativePath)}><CloseIcon className="stream-upload-modal__remove-icon" /></IconButton>
                            </Tooltip>
                          </div>
                        );
                      })}
                      {remaining > 0 ? (
                        <div className="stream-upload-modal__show-more">
                          <Button size="small" color="primary" onClick={() => setVisibleCounts(prev => ({ ...prev, [group.key]: visible + FOLDER_PAGE_SIZE }))}>
                            Show {Math.min(FOLDER_PAGE_SIZE, remaining).toLocaleString()} more ({remaining.toLocaleString()} remaining)
                          </Button>
                        </div>
                      ) : null}
                    </Collapse>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
        {checkingResume && items.length ?
          <div className="stream-upload-modal__check">
            <Typography variant="caption" color="textSecondary">Checking the files already in this upload...</Typography>
            <LinearProgress />
          </div> : null
        }
        {resumeCheck && resumeCheck.blocked ?
          <Alert severity="error" className="stream-upload-modal__check">
            {resumeCheck.activeElsewhere.length && !resumeCheck.extra.length && !resumeCheck.missing.length && resumeCheck.countMatches ?
              'This upload was active in the last minute and may still be running on another device. Try again in a minute.' :
              'This selection doesn\'t match the interrupted upload. Clear it and choose the original files, without renaming, moving or adding any. Files that already finished can be left out.'}
            {!resumeCheck.countMatches ?
              <div className="stream-upload-modal__problem-list">
                Expected <b>{resumeCheck.expectedFiles.toLocaleString()} files</b>, selected <b>{items.length.toLocaleString()}</b>.
              </div> : null
            }
            <ProblemList title="Not part of this upload" paths={resumeCheck.extra.length ? resumeCheck.extra : resumeCheck.leftOut} />
            <ProblemList title="Missing from your selection" paths={resumeCheck.missing} />
            <ProblemList title="Still being uploaded elsewhere" paths={resumeCheck.activeElsewhere} />
          </Alert> : null
        }
        {resumeCheck && !resumeCheck.blocked ?
          <Alert severity={resumeCheck.changed.length ? 'warning' : 'success'} className="stream-upload-modal__check">
            All files belong to this upload: <b>{resumeCheck.done.toLocaleString()}</b> already uploaded will be skipped, <b>{resumeCheck.toUpload.toLocaleString()}</b> will be uploaded.
            <ProblemList title="Changed since the upload started, will be uploaded again from the start" paths={resumeCheck.changed} />
            <ProblemList title="Large files that were partly uploaded, will start again" paths={resumeCheck.partial} />
            <ProblemList title="Not part of this upload, will be left out (for example skipped duplicates)" paths={resumeCheck.leftOut} />
            {resumeCheck.shortfall ?
              <div className="stream-upload-modal__problem-list">
                <b>{resumeCheck.shortfall.toLocaleString()} file{resumeCheck.shortfall > 1 ? 's' : ''} from the original selection {resumeCheck.shortfall > 1 ? 'are' : 'is'} no longer in this folder</b> and will be left out. The upload will finish with {resumeCheck.adjustTotals.totalFiles.toLocaleString()} files.
              </div> : null
            }
          </Alert> : null
        }
      </DialogContent>
      <DialogActions>
        <Button color="primary" disabled={scanning || starting} onClick={handleClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={scanning || starting || !items.length || checkingResume || !!(resumeCheck && resumeCheck.blocked)}
          onClick={startUpload}
        >
          {starting ? 'Starting...' : resumeSession ? 'Resume Upload' : 'Start Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StreamUploadModal;
